import React, { useState, useEffect } from 'react';
import { Form, Card, Button } from 'react-bootstrap';
import { getAvailableTimeSlots } from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import AlertMessage from '../../common/AlertMessage';

const DateTimeSelection = ({ 
  selectedDate, 
  selectedTime, 
  onDateTimeSelect, 
  selectedBarber, 
  selectedServices,
  corporationId 
}) => {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(selectedDate || '');
  const [dateError, setDateError] = useState(null);

  // Check if a date string is today
  const isToday = (dateString) => {
    if (!dateString) return false;
    const today = new Date();
    const selectedDate = new Date(dateString);
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    return today.getTime() === selectedDate.getTime();
  };

  // Get tomorrow's date as minimum date (no same-day bookings)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Get maximum date (e.g., 3 months from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  // Validate initial date prop when it changes
  useEffect(() => {
    if (selectedDate && isToday(selectedDate)) {
      setDateError('Sorry, bookings cannot be made for today. Please select a date starting from tomorrow.');
      setDate('');
      setAvailableSlots([]);
    } else if (selectedDate && selectedDate !== date) {
      // Only update if it's a valid date (not today)
      if (!isToday(selectedDate)) {
        setDate(selectedDate);
        setDateError(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!date || !selectedBarber || !selectedServices.length || !corporationId) {
        setAvailableSlots([]);
        setError(null);
        if (!date) {
          setError(null); // No error if just waiting for date selection
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const serviceIds = selectedServices.map(s => s.id);
        const response = await getAvailableTimeSlots(
          corporationId,
          selectedBarber.id,
          date,
          serviceIds
        );
        
        if (response && response.success) {
          // API returns slots in data.slots array (not available_slots)
          const slots = response.data?.slots || response.data?.available_slots || [];
          
          if (slots.length > 0) {
            // Slots are objects with start_time, end_time, and formatted properties
            // Store the full slot objects for later use
            setAvailableSlots(slots);
            setError(null);
          } else {
            setError('No available time slots for this date.');
            setAvailableSlots([]);
          }
        } else if (response && !response.success) {
          // API returned success: false
          const errorMsg = response.message || response.data?.message || 'Failed to load available time slots';
          setError(errorMsg);
          setAvailableSlots([]);
        } else {
          // Unexpected response structure
          console.warn('Unexpected response structure:', response);
          setError('Unexpected response from server. Please check console for details.');
          setAvailableSlots([]);
        }
      } catch (err) {
        console.error('Time slots error:', err);
        console.error('Error response:', err.response);
        console.error('Error response data:', err.response?.data);
        console.error('Error status:', err.response?.status);
        
        let errorMessage = 'Error loading available time slots. Please try again.';
        
        if (err.response) {
          // Server responded with error
          if (err.response.data) {
            if (err.response.data.message) {
              errorMessage = err.response.data.message;
            } else if (err.response.data.errors) {
              // Handle validation errors
              const errors = err.response.data.errors;
              if (typeof errors === 'object') {
                errorMessage = Object.values(errors).flat().join(', ');
              } else {
                errorMessage = String(errors);
              }
            } else if (err.response.data.error) {
              errorMessage = err.response.data.error;
            }
          }
          errorMessage += ` (Status: ${err.response.status})`;
        } else if (err.request) {
          // Request was made but no response received
          errorMessage = 'No response from server. Please check if the API is running.';
        } else {
          // Something else happened
          errorMessage = err.message || errorMessage;
        }
        
        setError(errorMessage);
        setAvailableSlots([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeSlots();
  }, [date, selectedBarber, selectedServices, corporationId]);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    
    // Check if the selected date is today
    if (isToday(newDate)) {
      setDateError('Sorry, bookings cannot be made for today. Please select a date starting from tomorrow.');
      setDate(''); // Clear the date
      setAvailableSlots([]); // Clear time slots
      if (onDateTimeSelect) {
        onDateTimeSelect('', null); // Clear date and time
      }
      return;
    }
    
    // Clear any previous date error
    setDateError(null);
    setDate(newDate);
    if (onDateTimeSelect) {
      onDateTimeSelect(newDate, null); // Clear time when date changes
    }
  };

  const handleTimeSelect = (time) => {
    if (onDateTimeSelect) {
      onDateTimeSelect(date, time);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (!selectedBarber) {
    return (
      <AlertMessage 
        variant="warning" 
        message="Please select a professional first." 
      />
    );
  }

  if (!selectedServices.length) {
    return (
      <AlertMessage 
        variant="warning" 
        message="Please select at least one service first." 
      />
    );
  }


  return (
    <div className="datetime-selection">
      <h6 className="mb-3">Select Date & Time</h6>
      
      {/* Date Selection Section - Top */}
      <Card className="mb-4">
        <Card.Body>
          <Form.Group>
            <Form.Label className="fw-bold mb-3">
              <i className="bi bi-calendar3 me-2"></i>
              Select Date
            </Form.Label>
            <Form.Control
              type="date"
              value={date}
              onChange={handleDateChange}
              min={getMinDate()}
              max={getMaxDate()}
              required
              isInvalid={!!dateError}
            />
            {dateError ? (
              <Form.Control.Feedback type="invalid" className="d-block">
                {dateError}
              </Form.Control.Feedback>
            ) : (
              <Form.Text className="text-muted">
                Bookings must be made at least one day in advance
              </Form.Text>
            )}
          </Form.Group>
        </Card.Body>
      </Card>

      {/* Available Time Slots Section - Bottom */}
      <Card>
        <Card.Body>
          <Form.Label className="fw-bold mb-3">
            <i className="bi bi-clock me-2"></i>
            Available Time Slots
          </Form.Label>
          
          {!date ? (
            <AlertMessage 
              variant="info" 
              message="Please select a date first to see available time slots." 
            />
          ) : loading ? (
            <LoadingSpinner />
          ) : error ? (
            <AlertMessage variant="danger" message={error} />
          ) : availableSlots.length === 0 ? (
            <AlertMessage 
              variant="warning" 
              message="No available time slots for this date. Please select another date." 
            />
          ) : (
            <div className="time-slots-container">
              <div>
                {availableSlots.map((slot, index) => {
                  // Handle both object format (with start_time) and string format
                  const slotTime = typeof slot === 'object' ? slot.start_time : slot;
                  const slotDisplay = typeof slot === 'object' ? slot.formatted : slot;
                  const isSelected = selectedTime === slotTime;
                  
                  return (
                    <Button
                      key={index}
                      variant={isSelected ? 'primary' : 'outline-primary'}
                      onClick={() => handleTimeSelect(slotTime)}
                      className="time-slot-btn"
                      title={typeof slot === 'object' ? `${slot.start_time} - ${slot.end_time}` : slot}
                    >
                      {slotDisplay}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default DateTimeSelection;
